#!/usr/bin/env node
/**
 * Piagnet CLI
 *
 * Commands:
 * - piagnet gateway start    Start the gateway server
 * - piagnet gateway stop     Stop the gateway server
 * - piagnet gateway status   Check gateway status
 * - piagnet config init      Initialize configuration
 * - piagnet config show      Show current configuration
 * - piagnet agent create     Create a new agent
 * - piagnet agent list       List all agents
 * - piagnet channel add      Add a channel
 * - piagnet channel list     List all channels
 */

import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { ConfigManager } from "./config/manager.js";
import { defaultConfig } from "./config/schema.js";
import { Gateway } from "./gateway/server.js";
import { SessionManager } from "./gateway/sessions.js";

const VERSION = "0.51.7";

/** CLI command handler */
async function main(): Promise<void> {
	const args = process.argv.slice(2);
	const command = args[0];
	const subcommand = args[1];

	if (!command || command === "--help" || command === "-h") {
		showHelp();
		process.exit(0);
	}

	if (command === "--version" || command === "-v") {
		console.log(`piagnet v${VERSION}`);
		process.exit(0);
	}

	try {
		switch (command) {
			case "gateway":
				await handleGateway(subcommand, args.slice(2));
				break;

			case "config":
				await handleConfig(subcommand, args.slice(2));
				break;

			case "agent":
				await handleAgent(subcommand, args.slice(2));
				break;

			case "channel":
				await handleChannel(subcommand, args.slice(2));
				break;

			case "doctor":
				await runDoctor();
				break;

			default:
				console.error(`Unknown command: ${command}`);
				showHelp();
				process.exit(1);
		}
	} catch (error) {
		console.error("Error:", error instanceof Error ? error.message : error);
		process.exit(1);
	}
}

/** Gateway commands */
async function handleGateway(subcommand: string | undefined, args: string[]): Promise<void> {
	const configManager = await ConfigManager.create();
	await configManager.load();

	switch (subcommand) {
		case "start": {
			const sessionManager = new SessionManager({
				sessionDir: configManager.getGatewayConfig().sessionDir,
			});
			await sessionManager.initialize();

			const gateway = new Gateway({ configManager, sessionManager });
			await gateway.start();

			console.log(`Gateway started on port ${configManager.getGatewayConfig().port}`);
			console.log("Press Ctrl+C to stop");

			// Keep running
			process.on("SIGINT", async () => {
				console.log("\nShutting down...");
				await gateway.stop();
				process.exit(0);
			});

			// Keep alive
			await new Promise(() => {});
			break;
		}

		case "stop":
			// For now, just show status
			console.log("Gateway stop not implemented (use Ctrl+C or kill process)");
			break;

		case "status": {
			// Check if port is in use
			const port = configManager.getGatewayConfig().port;
			const isRunning = await checkPortInUse(port);
			console.log(`Gateway status: ${isRunning ? "running" : "stopped"}`);
			console.log(`Port: ${port}`);
			break;
		}

		case "restart":
			console.log("Gateway restart not implemented");
			break;

		default:
			console.log("Usage: piagnet gateway <start|stop|status|restart>");
	}
}

/** Config commands */
async function handleConfig(subcommand: string | undefined, args: string[]): Promise<void> {
	const configManager = new ConfigManager();

	switch (subcommand) {
		case "init": {
			const created = await configManager.createInitialConfig();
			if (created) {
				console.log(`Created configuration at ${configManager.getConfigPath()}`);
				console.log("Edit this file to configure your agents and channels.");
			} else {
				console.log(`Configuration already exists at ${configManager.getConfigPath()}`);
			}
			break;
		}

		case "show": {
			await configManager.load();
			const config = configManager.getConfig();
			console.log(JSON.stringify(config, null, 2));
			break;
		}

		case "validate": {
			await configManager.load();
			const result = configManager.validate();
			if (result.valid) {
				console.log("Configuration is valid");
			} else {
				console.error("Configuration errors:");
				for (const error of result.errors) {
					console.error(`  - ${error}`);
				}
				process.exit(1);
			}
			break;
		}

		case "path": {
			console.log(configManager.getConfigPath());
			break;
		}

		default:
			console.log("Usage: piagnet config <init|show|validate|path>");
	}
}

/** Agent commands */
async function handleAgent(subcommand: string | undefined, args: string[]): Promise<void> {
	const configManager = await ConfigManager.create();
	await configManager.load();

	switch (subcommand) {
		case "list": {
			const agents = configManager.getConfig().agents;
			if (agents.length === 0) {
				console.log("No agents configured");
			} else {
				console.log("Agents:");
				for (const agent of agents) {
					const defaultMarker = configManager.getConfig().defaults.agent === agent.id ? " (default)" : "";
					console.log(`  - ${agent.id}: ${agent.name}${defaultMarker}`);
					console.log(`      Model: ${agent.model.provider}/${agent.model.model}`);
				}
			}
			break;
		}

		case "create": {
			const id = args[0];
			if (!id) {
				console.error("Usage: piagnet agent create <id>");
				process.exit(1);
			}

			const existing = configManager.getAgent(id);
			if (existing) {
				console.error(`Agent ${id} already exists`);
				process.exit(1);
			}

			configManager.upsertAgent({
				id,
				name: id,
				model: {
					provider: "anthropic",
					model: "claude-3-5-sonnet-20241022",
				},
				skills: [],
				session: {
					historyLimit: 100,
					idleMinutes: 60,
					dailyResetHour: 4,
					compression: "summary",
					queuing: "sequential",
				},
				bootstrap: {
					files: ["AGENTS.md", "SOUL.md", "USER.md", "IDENTITY.md", "TOOLS.md"],
					maxChars: 20000,
				},
				memory: {
					enabled: true,
					files: ["MEMORY.md"],
				},
			});

			await configManager.save();
			console.log(`Created agent: ${id}`);
			break;
		}

		case "delete": {
			const id = args[0];
			if (!id) {
				console.error("Usage: piagnet agent delete <id>");
				process.exit(1);
			}

			const removed = configManager.removeAgent(id);
			if (removed) {
				await configManager.save();
				console.log(`Deleted agent: ${id}`);
			} else {
				console.error(`Agent ${id} not found`);
				process.exit(1);
			}
			break;
		}

		default:
			console.log("Usage: piagnet agent <list|create|delete>");
	}
}

/** Channel commands */
async function handleChannel(subcommand: string | undefined, args: string[]): Promise<void> {
	const configManager = await ConfigManager.create();
	await configManager.load();

	switch (subcommand) {
		case "list": {
			const channels = configManager.getConfig().channels;
			if (channels.length === 0) {
				console.log("No channels configured");
			} else {
				console.log("Channels:");
				for (const channel of channels) {
					const status = channel.enabled ? "enabled" : "disabled";
					console.log(`  - ${channel.type}: ${channel.name} (${status})`);
				}
			}
			break;
		}

		case "add": {
			const type = args[0];
			if (!type) {
				console.error("Usage: piagnet channel add <type>");
				process.exit(1);
			}

			const existing = configManager.getChannel(type);
			if (existing) {
				console.error(`Channel ${type} already exists`);
				process.exit(1);
			}

			configManager.upsertChannel({
				type,
				name: type,
				enabled: false,
				config: {},
				access: {
					dmPolicy: "pairing",
					groupPolicy: "mention",
				},
			});

			await configManager.save();
			console.log(`Added channel: ${type}`);
			console.log(`Edit ${configManager.getConfigPath()} to configure the channel.`);
			break;
		}

		case "remove": {
			const type = args[0];
			if (!type) {
				console.error("Usage: piagnet channel remove <type>");
				process.exit(1);
			}

			const removed = configManager.removeChannel(type);
			if (removed) {
				await configManager.save();
				console.log(`Removed channel: ${type}`);
			} else {
				console.error(`Channel ${type} not found`);
				process.exit(1);
			}
			break;
		}

		default:
			console.log("Usage: piagnet channel <list|add|remove>");
	}
}

/** Doctor - diagnostic checks */
async function runDoctor(): Promise<void> {
	console.log("Running diagnostic checks...\n");

	let issues = 0;

	// Check Node.js version
	const nodeVersion = process.version;
	const majorVersion = parseInt(nodeVersion.slice(1).split(".")[0]);
	if (majorVersion < 20) {
		console.log("❌ Node.js version should be >= 20");
		console.log(`   Current: ${nodeVersion}`);
		issues++;
	} else {
		console.log(`✓ Node.js version: ${nodeVersion}`);
	}

	// Check configuration
	const configManager = new ConfigManager();
	try {
		await configManager.load();
		const result = configManager.validate();
		if (result.valid) {
			console.log("✓ Configuration is valid");
		} else {
			console.log("❌ Configuration has errors:");
			for (const error of result.errors) {
				console.log(`   - ${error}`);
			}
			issues++;
		}
	} catch (error) {
		console.log("⚠ Configuration not found (run 'piagnet config init')");
	}

	// Check session directory
	const sessionDir = configManager.getGatewayConfig().sessionDir;
	try {
		await fs.access(sessionDir);
		console.log(`✓ Session directory exists: ${sessionDir}`);
	} catch {
		console.log(`⚠ Session directory will be created: ${sessionDir}`);
	}

	console.log("\n" + (issues === 0 ? "All checks passed!" : `${issues} issue(s) found`));
}

/** Check if port is in use */
async function checkPortInUse(port: number): Promise<boolean> {
	// This is a simple check - in a real implementation,
	// we might want to check if Piagnet specifically is running
	return false;
}

/** Show help */
function showHelp(): void {
	console.log(`
piagnet v${VERSION} - Multi-channel AI Gateway

Usage: piagnet <command> [options]

Commands:
  gateway <start|stop|status|restart>  Manage gateway server
  config  <init|show|validate|path>    Configuration management
  agent   <list|create|delete>        Agent management
  channel <list|add|remove>           Channel management
  doctor                              Run diagnostic checks

Options:
  -h, --help     Show help
  -v, --version  Show version

Examples:
  piagnet config init
  piagnet gateway start
  piagnet agent create my-assistant
  piagnet channel add telegram
`);
}

// Run CLI
main();
